import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/core/Button';
import { Badge } from '@/shared/ui/core/Badge';
import type { SemanticTone } from '@/shared/ui/core/types';

export type FilterChip = {
  id: string;
  label: string;
  tone?: SemanticTone;
};

export type FilterBarProps = {
  children?: ReactNode;
  chips?: FilterChip[];
  onRemoveChip?: (id: string) => void;
  onClearAll?: () => void;
  className?: string;
  /** `row` = TestUI compact search + filters on one line. */
  fieldsLayout?: 'grid' | 'row';
};

export function FilterBar({
  children,
  chips = [],
  onRemoveChip,
  onClearAll,
  className,
  fieldsLayout = 'grid',
}: FilterBarProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-control border border-border bg-surface p-3 shadow-xs',
        className,
      )}
    >
      {children ? (
        <div
          className={
            fieldsLayout === 'row'
              ? 'flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between'
              : 'grid gap-3 sm:grid-cols-2 lg:grid-cols-4'
          }
        >
          {children}
        </div>
      ) : null}
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className="inline-flex items-center gap-1 rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
              onClick={() => onRemoveChip?.(chip.id)}
              aria-label={`Xóa bộ lọc ${chip.label}`}
            >
              <Badge tone={chip.tone ?? 'primary'} label={chip.label} icon={<X className="size-3" />} />
            </button>
          ))}
          {onClearAll ? (
            <Button variant="ghost" size="sm" onClick={onClearAll}>
              Xóa tất cả
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
